import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, formatDateAT } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import { AlertOctagon, CalendarClock, RotateCw } from "lucide-react";

export default function UpcomingPayments() {
  const { lang } = useI18n();
  const { periodKey } = usePeriod();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!periodKey) return;
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/upcoming-payments?period=${periodKey}`);
        setData(data);
      } catch {
        setData({ ap_items: [], recurring_items: [] });
      }
    })();
  }, [periodKey]);

  const t = (tr, de) => (lang === "de" ? de : tr);
  if (!data) return null;

  const items = data.ap_items || [];
  const recurring = data.recurring_items || [];
  const isEmpty = items.length === 0 && recurring.length === 0;

  return (
    <div className="rounded-xl border bg-background" data-testid="upcoming-payments">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="text-sm font-semibold flex items-center gap-2">
          <CalendarClock className="w-4 h-4" /> {t("Yaklaşan Ödemeler", "Fällige Zahlungen")}
        </div>
        {data.overdue_count > 0 && (
          <Badge className="bg-destructive hover:bg-destructive gap-1">
            <AlertOctagon className="w-3 h-3" /> {data.overdue_count} {t("gecikmiş", "überfällig")}
          </Badge>
        )}
      </div>
      {isEmpty ? (
        <div className="p-4">
          <EmptyState
            icon={CalendarClock}
            title={t("Yaklaşan ödeme yok", "Keine anstehenden Zahlungen")}
            description={t("Açık tedarikçi faturası ve sabit ödeme yok.", "Keine offenen Rechnungen oder Daueraufträge.")}
          />
        </div>
      ) : (
        <ul className="divide-y">
          {items.slice(0, 6).map((it) => {
            const overdue = it.status === "overdue";
            return (
              <li key={it.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`upcoming-${it.id}`}>
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${overdue ? "text-destructive bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                  <CalendarClock className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {it.party_name || it.number || "—"}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-2">
                    {it.due_date ? formatDateAT(it.due_date) : "—"}
                    {overdue && <Badge className="h-4 text-[9px] px-1 bg-destructive hover:bg-destructive">{t("Gecikmiş", "Überfällig")}</Badge>}
                    {typeof it.days_to_due === "number" && !overdue && (
                      <span>· {it.days_to_due} {t("gün", "Tage")}</span>
                    )}
                  </div>
                </div>
                <div className="font-mono text-sm font-semibold text-destructive">
                  {money(it.amount_total || 0)}
                </div>
              </li>
            );
          })}
          {recurring.slice(0, 4).map((r) => (
            <li key={`rec-${r.id}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`recurring-${r.id}`}>
              <div className="w-8 h-8 rounded-lg border flex items-center justify-center text-slate-700 bg-slate-50 border-slate-200">
                <RotateCw className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.vendor}</div>
                <div className="text-[11px] text-muted-foreground font-mono">
                  {t("Sabit", "Wiederkehrend")} · {r.recurring_period}
                </div>
              </div>
              <div className="font-mono text-sm font-semibold">
                {money(r.amount || 0)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
